//TODO: this should be in sync with electron version

import { type CustomDimensions } from '@/features/telemetry/types'

const sensitiveKeywords = [
	//
	'credential',
	'credentials',
	'password',
	'clientid',
	'tenantid',
	'secret',
	'token',
	'cookie'
]

const sensitiveValues = ['ibit-']

const ANY_EVENT = '*'

/**
 * Map of keywords which normally are treated as suspicious, but in fact are acceptable
 * Values of the map are event names for which given keyword is acceptable in telemetry
 * or an asterisk if keyword is acceptable for any event
 */
const whitelistedEntries = [
	...new Map<string, string | string[]>([
		['tokenExpiration', ANY_EVENT],
		['tokenExpiresIn', ANY_EVENT],
		['isTokenExpired', ANY_EVENT],
		['newPowerbiToken', ANY_EVENT],
		['obfuscatedToken', ANY_EVENT],
		['needsToChangePassword', ANY_EVENT],
		['isPowerBITokenDefined', ANY_EVENT],
		['powerBITokenExpiration', ANY_EVENT],
		['powerBITokenExpiresIn', ANY_EVENT],
		['isPowerbiTokenServiceRunning', ANY_EVENT],
		['powerbiConfigVersionFromToken', ANY_EVENT],
		['keepCurrentTokenValue', ANY_EVENT],
		['expandCredentialsForm', ANY_EVENT],
		['password', ['User pressed the login button', 'User pressed agree and sign up during registration.']],
		['passwordConfirm', 'User pressed agree and sign up during registration.'],
		['passwordStrength', 'User pressed agree and sign up during registration.'],
		['oldPasswordErrors', 'Cannot change password in application settings due to validation errors'],
		[
			'passwordErrors',
			[
				'Cannot change password in application settings due to validation errors',
				'Password cannot be changed due to form validation errors',
				'Cannot finish signup process due to validation errors.',
				'Cannot login due to validation errors'
			]
		],
		[
			'passwordConfirmErrors',
			[
				'Cannot change password in application settings due to validation errors',
				'Password cannot be changed due to form validation errors',
				'Cannot finish signup process due to validation errors.'
			]
		],
		['tokenErrors', 'Password cannot be changed due to form validation errors'],
		['passwordStrengthErrors', 'Cannot finish signup process due to validation errors.']
	]).entries()
].reduce((result, [key, value]) => {
	const normalizedValue = Array.isArray(value) ? value : [value]

	result.set(
		key.toLowerCase(),
		normalizedValue.map((v) => v.toLowerCase())
	)

	return result
}, new Map<string, string[]>())

const whitelistedValues: string[] = [
	//
	'Xzibit-Restless' // just for demonstration purposes
].map((keyword) => keyword.toLowerCase())

export const SENSITIVE_TELEMETRY_MESSAGE = 'Potentially sensitive telemetry found.'

const REDACTED_MESSAGE = 'Redacted for security purposes'

function isWhitelistedKeyword(keyword: string, eventName: string) {
	const event = whitelistedEntries.get(keyword) ?? []

	return event.includes(ANY_EVENT) || event.includes(eventName.toLowerCase())
}

function getSanitizedValue(
	eventName: string,
	input: unknown,
	incomingSuspiciousKeywords: string[] = []
): { value: unknown; suspiciousKeywords: string[] } {
	let suspiciousKeywords = [...incomingSuspiciousKeywords]

	if (!input) {
		return { value: input, suspiciousKeywords }
	}

	let asArray: unknown[] | undefined

	if (input instanceof Set) {
		asArray = Array.from(input)
	} else if (Array.isArray(input)) {
		asArray = input
	}

	if (asArray) {
		const sanitized = asArray.map((item) => getSanitizedValue(eventName, item))

		suspiciousKeywords = [...suspiciousKeywords, ...sanitized.flatMap((sanitizedArrayItem) => sanitizedArrayItem.suspiciousKeywords)]

		return {
			value: sanitized.map(({ value }) => value),
			suspiciousKeywords
		}
	}

	let asObject: object | undefined

	if (input instanceof Map) {
		asObject = Object.fromEntries(input.entries())
	} else if (typeof input === 'object') {
		asObject = input
	}

	if (asObject) {
		const sanitizedObject = Object.entries(asObject).reduce((accumulated, [key, value]) => {
			const normalizedKey = key.toLowerCase().trim()
			const matchingSensitiveKeyword = sensitiveKeywords.find(
				(sensitiveKeyword) => !isWhitelistedKeyword(normalizedKey, eventName) && normalizedKey.includes(sensitiveKeyword)
			)

			if (matchingSensitiveKeyword) {
				suspiciousKeywords.push(key)

				return {
					...accumulated,
					[key]: REDACTED_MESSAGE
				}
			}

			const sanitized = getSanitizedValue(eventName, value)

			suspiciousKeywords = [...suspiciousKeywords, ...sanitized.suspiciousKeywords]

			return {
				...accumulated,
				[key]: sanitized.value
			}
		}, {})

		return { value: sanitizedObject, suspiciousKeywords }
	}

	let asString: string | undefined

	if (input instanceof RegExp) {
		asString = input.source
	} else if (input instanceof Error) {
		asString = input.message
	} else if (typeof input === 'string') {
		asString = input
	}

	if (asString) {
		const normalizedValue = asString.toLowerCase().trim()
		const matchingSensitiveValue = sensitiveValues.find(
			(sensitiveValue) => !whitelistedValues.includes(normalizedValue) && normalizedValue.includes(sensitiveValue)
		)

		if (matchingSensitiveValue) {
			return { value: REDACTED_MESSAGE, suspiciousKeywords: [...suspiciousKeywords, matchingSensitiveValue] }
		}
	}

	return { value: input, suspiciousKeywords }
}

function filterSensitiveFields(
	eventName: string,
	object: CustomDimensions
): {
	result: Record<string, unknown>
	suspiciousKeywords: string[]
} {
	if (!object) {
		return {
			result: object,
			suspiciousKeywords: []
		}
	}

	return Object.entries(object).reduce<{
		result: Record<string, unknown>
		suspiciousKeywords: string[]
	}>(
		(accumulated, [key, value]) => {
			const normalizedKey = key.toLowerCase().trim()
			const matchingSensitiveKeyword = sensitiveKeywords.find(
				(sensitiveKeyword) => !isWhitelistedKeyword(normalizedKey, eventName) && normalizedKey.includes(sensitiveKeyword)
			)

			if (matchingSensitiveKeyword) {
				return {
					result: {
						...accumulated.result,
						[key]: REDACTED_MESSAGE
					},
					suspiciousKeywords: [...accumulated.suspiciousKeywords, key]
				}
			}

			const sanitizedValue = getSanitizedValue(eventName, value)

			return {
				result: {
					...accumulated.result,
					[key]: sanitizedValue.value
				},
				suspiciousKeywords: [...accumulated.suspiciousKeywords, ...sanitizedValue.suspiciousKeywords]
			}
		},
		{
			result: {},
			suspiciousKeywords: []
		}
	)
}

export function getVerifiedTelemetryEvent(
	eventName: string,
	payload?: CustomDimensions
): { isSafe: true } | { isSafe: false; redactedPayload: Record<string, unknown>; suspiciousKeywords: string[] } {
	if (!payload) {
		return {
			isSafe: true
		}
	}

	const sanitized = filterSensitiveFields(eventName, payload)

	if (sanitized.suspiciousKeywords.length > 0) {
		return {
			isSafe: false,
			suspiciousKeywords: sanitized.suspiciousKeywords,
			redactedPayload: sanitized.result
		}
	}

	return {
		isSafe: true
	}
}

export const obfuscate = (text?: string, numberOfInitialChars?: number) =>
	text ? `${numberOfInitialChars ? text.slice(0, numberOfInitialChars) : ''}... (obfuscated, ${(text ?? '').length} chars)` : ''

export const stringifyFieldForTelemetry = (value: unknown): string => {
	if (value === null) {
		return 'null'
	}

	if (value === undefined) {
		return 'undefined'
	}

	if (typeof value === 'string') {
		return value
	}

	if (typeof value === 'number' || typeof value === 'boolean') {
		return value.toString()
	}

	if (value instanceof Date) {
		return value.toISOString()
	}

	if (Array.isArray(value)) {
		return JSON.stringify(value)
	}

	if (typeof value === 'object') {
		return JSON.stringify(value)
	}

	return 'unknown'
}
